/* ============================================================
   collection.js — the filter and sort layer.

   Loaded only on collection and search pages (layout/theme.liquid).

   Two elements, and deliberately nothing else:

   1. <nmp-facets> — upgrades the filter bar from "a form you submit" into
      "a form that submits itself and swaps the results in place". Everything
      it does is an enhancement of markup that already works: the bar is a real
      GET form (snippets/facet-bar.liquid), so with this file absent, blocked,
      or still loading, filtering and sorting still work by page load and every
      state still has its own address.

   2. <nmp-filter-sheet> — the mobile bottom drawer, a subclass of the shared
      <nmp-overlay> in nmp.js. Focus trap, Esc, scrim click, aria-expanded and
      the body scroll lock all come from there rather than being written twice;
      assets/nmp.js reserves the base class for exactly this.

   How a swap works. The bar never knows about the grid in Liquid — the grid
   marks itself with data-facet-results and this file finds it in the DOM. Both
   sections are re-fetched through Shopify's Section Rendering API and only the
   inside of each is replaced, never the section wrapper itself, because the
   theme editor binds to that wrapper and replacing it breaks the editor.

   Because the markup comes back rendered by Liquid, the result count, the
   remaining filter values, their counts, the active chips, the pagination and
   the empty state are all server truth. There is no client-side model of the
   collection to drift out of sync.

   Last updated: 2026-09-07
   ============================================================ */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px)';

  /* Every swappable region carries id="section-<id>", written by
     snippets/section-style.liquid. That one convention is what lets us find
     the same region in the live page and in a fetched response. */
  function regionId(id) {
    return 'section-' + id;
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var self = this;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /* ---------------------------------------------------------- facets */

  class NmpFacets extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.form = this.querySelector('[data-facet-form]');
      if (!this.form) return;
      this._ready = true;

      this.sectionId = this.getAttribute('data-section-id');
      this.sheet = this.querySelector('nmp-filter-sheet');
      this.desktop = window.matchMedia(DESKTOP);

      this._onChange = debounce(this._change.bind(this), 250);
      this._onSubmit = this._submit.bind(this);
      this._onPop = this._pop.bind(this);
      this._onToggle = this._toggle.bind(this);
      this._onDocClick = this._docClick.bind(this);
      this._onKeydown = this._keydown.bind(this);

      this.form.addEventListener('change', this._onChange);
      this.form.addEventListener('submit', this._onSubmit);
      this.addEventListener('click', this._onLink.bind(this));
      /* `toggle` does not bubble, so it has to be caught on the way down. */
      this.addEventListener('toggle', this._onToggle, true);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKeydown);
      window.addEventListener('popstate', this._onPop);

      /* This is what swaps the mobile stack for a drawer and hides the now
         redundant submit button. Nothing above depends on it, so a failure
         earlier in this method leaves the plain form intact. */
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('popstate', this._onPop);
      if (this._controller) this._controller.abort();
    }

    get sheetIsOpen() {
      return !!(this.sheet && this.sheet.hasAttribute('data-open'));
    }

    /* ---------------------------------------------------- events */

    _change() {
      /* While the drawer is open the shopper is still choosing. Submitting on
         every tick would close it under them and cost a request per tap, so
         mobile waits for "show results" and desktop applies immediately. */
      if (this.sheetIsOpen) return;
      this._apply(this._query());
    }

    _submit(event) {
      event.preventDefault();
      var query = this._query();
      if (this.sheet && this.sheetIsOpen) this.sheet.close();
      this._apply(query);
    }

    /* Active-filter chips and "clear all" are ordinary links to a filtered
       URL. Intercepting them keeps the page from reloading; ignoring the
       interception (JS off, middle click, modifier held) still works. */
    _onLink(event) {
      var link = event.target.closest('a[href]');
      if (!link) return;
      if (!link.closest('.facetbar__chips') && !link.hasAttribute('data-facet-clear')) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this._apply(link.getAttribute('href').split('?')[1] || '');
    }

    _pop() {
      /* The address bar is now the truth. Re-render from it rather than from
         the form, whose inputs still show the state we just navigated away
         from — the server will send back the correct ones. */
      this._apply(window.location.search.replace(/^\?/, ''), false);
    }

    _toggle(event) {
      if (!this.desktop.matches) return;
      var group = event.target;
      if (!group.hasAttribute || !group.hasAttribute('data-facet') || !group.open) return;
      Array.prototype.forEach.call(this.querySelectorAll('[data-facet][open]'), function (other) {
        if (other !== group) other.open = false;
      });
    }

    _docClick(event) {
      if (!this.desktop.matches) return;
      if (this.contains(event.target)) return;
      this._closeGroups();
    }

    _keydown(event) {
      if (event.key !== 'Escape' || !this.desktop.matches) return;
      if (!this.querySelector('[data-facet][open]')) return;
      this._closeGroups();
    }

    _closeGroups() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-facet][open]'), function (group) {
        group.open = false;
      });
    }

    /* ---------------------------------------------------- query */

    _query() {
      var params = new URLSearchParams();
      new FormData(this.form).forEach(function (value, key) {
        /* An empty price field would send `filter.v.price.gte=` and filter the
           collection down to nothing. Blank means "no opinion". */
        if (String(value).trim() === '') return;
        params.append(key, value);
      });
      return params.toString();
    }

    /* ---------------------------------------------------- swap */

    _targets() {
      var ids = [this.sectionId];
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-facet-results][data-section-id]'),
        function (el) {
          ids.push(el.getAttribute('data-section-id'));
        }
      );
      return ids;
    }

    _apply(query, push) {
      var self = this;
      var ids = this._targets();
      var url = window.location.pathname + (query ? '?' + query : '');
      var state = this._capture();

      if (this._controller) this._controller.abort();
      var controller = new AbortController();
      this._controller = controller;
      var signal = controller.signal;

      this._busy(true);

      Promise.all(
        ids.map(function (id) {
          /* One request per section. The Section Rendering API takes a single
             id, and asking for each separately means a section that fails to
             render cannot take the others down with it. */
          var sep = query ? '&' : '?';
          return window
            .fetch(url + sep + 'section_id=' + encodeURIComponent(id), { signal: signal })
            .then(function (response) {
              if (!response.ok) throw new Error('section ' + id);
              return response.text();
            })
            .then(function (text) {
              return { id: id, text: text };
            });
        })
      )
        .then(function (results) {
          var parser = new DOMParser();
          results.forEach(function (result) {
            var doc = parser.parseFromString(result.text, 'text/html');
            var incoming = doc.getElementById(regionId(result.id));
            var live = document.getElementById(regionId(result.id));
            /* Taking part of the response means taking its stylesheet by hand;
               Shopify only ships a page the CSS of the files it rendered.
               Nothing on a collection page needs this today — every file a
               filter change can produce was already in the first render — but
               a future filter that reveals a card variant would, and the cost
               is one call. See assets/nmp.js.

               Before the swap, not after: adopting afterwards leaves the
               browser a frame in which the new markup has no rules, so the
               grid would flash unstyled on the render this exists to protect. */
            if (window.NmpAdoptSectionStyles) window.NmpAdoptSectionStyles(doc, result.id);
            /* innerHTML only — the #shopify-section-* wrapper around this is
               what the theme editor binds to, and replacing it is what makes a
               section stop responding to the editor. */
            if (incoming && live) live.innerHTML = incoming.innerHTML;
          });

          /* A live region has to OUTLIVE the update it describes. The count
             line lives inside the region that was just replaced wholesale, so
             screen readers saw a new element rather than a changed one and
             announced nothing — which is exactly the trap cart-drawer.liquid
             documents and sidesteps by keeping its status line outside
             [data-cart-root]. The grid and the results section now emit a
             persistent [data-facet-status] outside their swap root, and this
             copies the fresh count into it. The visible count stays where it
             is; it is still what focus moves to. */
          var status = document.querySelector('[data-facet-status]');
          var count = document.querySelector('[data-facet-count]');
          if (status && count) status.textContent = count.textContent.trim();

          if (push !== false) window.history.pushState({ nmpFacets: true }, '', url);
          self._restore(state);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          /* Anything else — offline, a 500, a section that failed to render —
             and the honest fallback is the thing that always works. */
          window.location.href = url;
        })
        .then(function () {
          /* Only the current request clears busy: a superseded filter
             rejects with AbortError, returns from the catch, then reached here
             anyway and cleared its successor's aria-busy. */
          if (self._controller !== controller) return;
          self._busy(false);
        });
    }

    _busy(state) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-facet-results]'), function (el) {
        if (state) el.setAttribute('aria-busy', 'true');
        else el.removeAttribute('aria-busy');
      });
    }

    /* Without this, every change on desktop closes the dropdown the shopper is
       working in and throws focus to the top of the page. */
    _capture() {
      var open = [];
      Array.prototype.forEach.call(this.querySelectorAll('[data-facet][open]'), function (group) {
        open.push(group.getAttribute('data-param'));
      });
      var active = document.activeElement;
      var inForm = active && this.contains(active);
      return {
        open: open,
        name: inForm ? active.getAttribute('name') : null,
        value: inForm ? active.value : null
      };
    }

    _restore(state) {
      /* This element was inside one of the regions we just replaced, so it no
         longer exists. Everything from here on works against its successor. */
      var next = document.querySelector('nmp-facets[data-section-id="' + this.sectionId + '"]');
      if (!next) return;

      /* Built from merchant filter values. One holding a double quote — a size
         written 5" — threw inside this .then(), and the .catch() below then
         reloaded the page after the DOM was already swapped. The quotes come
         off with the escaping: CSS.escape emits an identifier. */
      state.open.forEach(function (param) {
        var group = next.querySelector('[data-facet][data-param=' + CSS.escape(param) + ']');
        if (group) group.open = true;
      });

      var restored = false;
      if (state.name) {
        var byName = '[name=' + CSS.escape(state.name) + ']';
        /* The value clause disambiguates one checkbox from its siblings, which
           all share a name. It CANNOT find a <select>: `value` on a select is a
           property, not a reflected attribute — the attribute lives on the
           <option> — so `[name=sort_by][value=price-ascending]` matches nothing
           and the sort control was the one thing in this bar that never got its
           focus back. A keyboard user re-sorted and was dropped on the count.

           Try the precise selector, then fall back to the name alone. A name
           that matches several elements resolves to the first, which is right
           for a select (there is one) and no worse than nothing for a
           checkbox. */
        var field = null;
        if (state.value) field = next.querySelector(byName + '[value=' + CSS.escape(state.value) + ']');
        if (!field) field = next.querySelector(byName);
        if (field) {
          field.focus({ preventScroll: true });
          restored = true;
        }
      }

      if (!restored) {
        /* The count is the shortest honest answer to "what just happened",
           and it is the one thing that always changes. The announcement comes
           from [data-facet-status] above, not from this element — moving
           focus here is about where the visitor lands, not about being
           heard. */
        var count = document.querySelector('[data-facet-count]');
        if (count) count.focus({ preventScroll: true });
      }
    }
  }

  /* ----------------------------------------------------- filter sheet */

  var Base = window.NmpOverlay;

  if (Base) {
    class NmpFilterSheet extends Base {
      connectedCallback() {
        super.connectedCallback();
        if (!this._onMq) {
          this._mq = window.matchMedia(DESKTOP);
          this._onMq = function () {
            /* Crossing into desktop turns the drawer back into an inline row.
               If it were still "open" the body would stay scroll-locked with
               no visible panel to close. */
            if (this._mq.matches) this.close({ restoreFocus: false });
          }.bind(this);
        }
        /* The same reference every time (2026-09-07): a reconnect used to
           build a fresh handler over the old one, and disconnectedCallback
           could only ever remove the newest. addEventListener de-duplicates
           an identical reference, so this re-arms rather than stacks. */
        this._mq.addEventListener('change', this._onMq);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this._mq) this._mq.removeEventListener('change', this._onMq);
      }
    }

    if (!window.customElements.get('nmp-filter-sheet')) {
      window.customElements.define('nmp-filter-sheet', NmpFilterSheet);
    }
  }

  if (!window.customElements.get('nmp-facets')) {
    window.customElements.define('nmp-facets', NmpFacets);
  }
})();
